import { useEffect, useState } from "react";
import { Layers, Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import ServiceIcon from "../../components/ServiceIcon";

const ICONS = ["Brain", "MessagesSquare", "Workflow", "Database", "Cloud", "Code2", "Sparkles"];
const EMPTY = { slug: "", title: "", tagline: "", description: "", icon: "Sparkles", features: "", order: 0 };

const toForm = (s) => ({ ...s, features: (s.features || []).join("\n") });
const toPayload = (f) => ({
  slug: f.slug.trim(), title: f.title, tagline: f.tagline, description: f.description,
  icon: f.icon, order: Number(f.order) || 0,
  features: f.features.split("\n").map((x) => x.trim()).filter(Boolean),
});

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/services").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const u = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (editing === "new") await api.post("/admin/services", payload);
      else await api.put(`/admin/services/${editing}`, payload);
      toast.success("Service saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this service?")) return;
    await api.delete(`/admin/services/${slug}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-services">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-primary" />
          <h2 className="font-heading text-xl font-semibold">Services ({items.length})</h2>
        </div>
        <button onClick={() => { setForm(EMPTY); setEditing("new"); }} data-testid="add-service" className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold px-4 py-2.5 transition-colors">
          <Plus className="w-4 h-4" /> New Service
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((s) => (
          <div key={s.slug} className="border border-white/10 bg-[#0F111A] p-5">
            <div className="w-10 h-10 grid place-items-center border border-primary/30 bg-primary/10 text-primary mb-4"><ServiceIcon name={s.icon} className="w-5 h-5" /></div>
            <h3 className="font-semibold text-white">{s.title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">/{s.slug}</p>
            <p className="text-sm text-slate-400 mt-2 line-clamp-2">{s.tagline}</p>
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setForm(toForm(s)); setEditing(s.slug); }} data-testid={`edit-service-${s.slug}`} className="inline-flex items-center gap-1.5 text-xs border border-white/10 px-3 py-1.5 text-slate-300 hover:border-primary hover:text-primary transition-colors"><Pencil className="w-3 h-3" /> Edit</button>
              <button onClick={() => remove(s.slug)} data-testid={`delete-service-${s.slug}`} className="inline-flex items-center gap-1.5 text-xs border border-white/10 px-3 py-1.5 text-slate-400 hover:border-red-400/40 hover:text-red-400 transition-colors"><Trash2 className="w-3 h-3" /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="min-h-full grid place-items-center p-4">
            <form onClick={(e) => e.stopPropagation()} onSubmit={save} data-testid="service-form" className="w-full max-w-xl bg-[#0F111A] border border-white/10 p-7 space-y-4 my-8">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-xl font-semibold">{editing === "new" ? "New" : "Edit"} Service</h3>
                <button type="button" onClick={() => setEditing(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <In label="Slug *"><input required disabled={editing !== "new"} value={form.slug} onChange={u("slug")} className={ic} placeholder="my-service" /></In>
                <In label="Order"><input type="number" value={form.order} onChange={u("order")} className={ic} /></In>
              </div>
              <In label="Title *"><input required value={form.title} onChange={u("title")} className={ic} /></In>
              <In label="Tagline"><input value={form.tagline} onChange={u("tagline")} className={ic} /></In>
              <In label="Icon">
                <div className="flex flex-wrap gap-2">
                  {ICONS.map((name) => (
                    <button type="button" key={name} onClick={() => setForm((f) => ({ ...f, icon: name }))} className={`w-10 h-10 grid place-items-center border transition-colors ${form.icon === name ? "border-primary text-primary bg-primary/10" : "border-white/10 text-slate-400 hover:text-white"}`}>
                      <ServiceIcon name={name} className="w-5 h-5" />
                    </button>
                  ))}
                </div>
              </In>
              <In label="Description"><textarea rows={4} value={form.description} onChange={u("description")} className={ic} /></In>
              <In label="Features (one per line)"><textarea rows={4} value={form.features} onChange={u("features")} className={ic} /></In>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} data-testid="service-form-submit" className="bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3 transition-colors disabled:opacity-60">{saving ? "Saving..." : "Save"}</button>
                <button type="button" onClick={() => setEditing(null)} className="border border-white/15 text-slate-300 px-6 py-3 hover:border-white/40 transition-colors">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const ic = "w-full bg-[#05060A] border border-white/10 focus:border-primary outline-none text-white px-3.5 py-2.5 text-sm transition-colors disabled:opacity-50";
const In = ({ label, children }) => (
  <label className="block"><span className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{label}</span>{children}</label>
);
